/**
 * Owns the boot screen that `index.html` paints before the bundle runs
 * (`#pre-app-loading`, a copy of SunriseBackground).
 *
 * Anything standing between the boot screen and the first real screen takes a
 * *hold*, and it goes away as soon as every hold is released.
 *
 * A hold is either an *entry* — a member on their way into the app, which is
 * what may start a fresh showing — or it merely keeps covering what
 * `index.html` already painted: a cold start into `/app`, or a native launch,
 * where the boot screen is up before there is any session at all. Only an
 * entry showing retires the boot screen, so a lazy chunk inside the running
 * app cannot cover it again, while a native launch cover leaves the member's
 * eventual login its own showing. Leaving the app ends the entry
 * (`rearmBootScreen`).
 *
 * `useBootScreenHold` classifies the holds; this module tracks them. Inert
 * until `startBootScreen` finds the overlay, so tests that mount the app never
 * touch it or leave timers behind.
 */

const OVERLAY_ID = "pre-app-loading";
const HIDDEN_CLASS = "pre-app-loading--hidden";
const ANIMATE_CLASS = "pre-app-loading--animate";

/** Safety net — a leaked hold must never trap the user behind the boot screen. */
const MAX_VISIBLE_MS = 10_000;

const noop = (): void => undefined;

const holds = new Set<symbol>();
let overlay: HTMLElement | undefined;
let visible = false;
let ready = false;
let coveringEntry = false;
let scheduledHide: ReturnType<typeof setTimeout> | undefined;
let watchdog: ReturnType<typeof setTimeout> | undefined;
let releaseBootHold: (() => void) | undefined;
let pinned = false;

/**
 * The routes the boot screen may cover. Mirrored by the inline gate in
 * index.html, which has to make the same call before the bundle exists.
 */
export const isBootScreenPath = (pathname: string): boolean =>
  pathname === "/" || /^\/app(\/|$)/.test(pathname);

/**
 * Plays the sunrise from the start. The class carrying the animation is dropped
 * and re-added around a layout read, which is what makes the browser treat it
 * as a new animation instead of the finished one it has already run.
 */
const restartSunrise = (element: HTMLElement): void => {
  element.classList.remove(ANIMATE_CLASS);
  void element.offsetWidth;
  element.classList.add(ANIMATE_CLASS);
};

const show = (): void => {
  if (visible || !overlay) {
    return;
  }
  visible = true;
  overlay.classList.remove(HIDDEN_CLASS);
  restartSunrise(overlay);
  if (!pinned) {
    watchdog = setTimeout(hide, MAX_VISIBLE_MS);
  }
};

/**
 * Hiding a visible boot screen retires it only if the showing covered an
 * entry into the app — later holds find `ready` set. A non-entry showing,
 * like the native launch cover, hides without retiring it.
 */
function hide(): void {
  if (!visible || !overlay || pinned) {
    return;
  }
  visible = false;
  ready = coveringEntry;
  coveringEntry = false;
  holds.clear();
  clearTimeout(watchdog);
  watchdog = undefined;
  overlay.classList.add(HIDDEN_CLASS);
}

/**
 * Hiding is deferred to the next task rather than run inline: React fires all
 * effect cleanups in a commit before the new effects, so the hand-off from one
 * hold to the next (auth gate to pending route, route to flag fetch) leaves a
 * moment with no holds at all. The next hold cancels this timer.
 */
const scheduleHideIfIdle = (): void => {
  if (holds.size > 0 || scheduledHide !== undefined) {
    return;
  }

  scheduledHide = setTimeout(() => {
    scheduledHide = undefined;
    hide();
  });
};

/**
 * Shows the boot screen, if it still has a showing left in it, and keeps it up
 * until the returned release function is called. `isEntry` marks whether this
 * hold covers a member's entry into the app, which is what lets it start a
 * fresh showing once the previous one has retired.
 */
export const holdBootScreen = (isEntry: boolean): (() => void) => {
  const canHold = visible || (isEntry && !ready);
  if (!overlay || !canHold) {
    return noop;
  }

  const token = Symbol(OVERLAY_ID);
  holds.add(token);

  if (isEntry) {
    coveringEntry = true;
  }

  clearTimeout(scheduledHide);
  scheduledHide = undefined;
  show();

  return () => {
    holds.delete(token);
    scheduleHideIfIdle();
  };
};

/**
 * Ends the current entry into the app, so the next one gets its own boot
 * screen: a member coming back from the signup funnel, or signing in again
 * after a logout, is entering the app afresh even though the page never
 * reloaded. Visibility is left to the holds — this only lifts the retirement.
 */
export const rearmBootScreen = (): void => {
  ready = false;
};

/**
 * Takes ownership of the boot screen, and holds whatever `index.html` painted
 * until React has mounted. When index.html left the overlay hidden the app is
 * starting on a public screen, so there is nothing to bridge — the overlay
 * waits for the member to enter the app.
 */
export const startBootScreen = (): void => {
  const element = document.getElementById(OVERLAY_ID);
  if (overlay || !element) {
    return;
  }

  overlay = element;
  if (!element.classList.contains(HIDDEN_CLASS)) {
    visible = true;
    watchdog = setTimeout(hide, MAX_VISIBLE_MS);
    releaseBootHold = holdBootScreen(false);
  }
};

/**
 * Releases boot's own hold, once React has committed its first render. Holds
 * taken by components during that render (the auth gate, pending lazy routes)
 * have already registered, so the boot screen stays up until they resolve.
 */
export const releaseBootScreen = (): void => {
  releaseBootHold?.();
  releaseBootHold = undefined;
};

/**
 * Debug-only: shows the boot screen and keeps it up indefinitely — no
 * watchdog, no hold accounting — so it can be inspected. The overlay covers
 * the debug panel itself, so clicking it is the way back out.
 */
export const pinBootScreen = (): void => {
  overlay ??= document.getElementById(OVERLAY_ID) ?? undefined;
  if (!overlay) {
    return;
  }
  clearTimeout(scheduledHide);
  scheduledHide = undefined;
  clearTimeout(watchdog);
  watchdog = undefined;
  pinned = true;
  visible = false;
  show();
  overlay.addEventListener("click", unpinBootScreen, { once: true });
};

export const unpinBootScreen = (): void => {
  if (!pinned) {
    return;
  }
  overlay?.removeEventListener("click", unpinBootScreen);
  pinned = false;
  hide();
};

export const isBootScreenPinned = (): boolean => pinned;
